<template>
    <!-- no DOM, we inject a canvas into the map container -->
    <span v-if="false"></span>
</template>

<script>
import { Deck } from "@deck.gl/core";
import { ArcLayer } from "@deck.gl/layers";

export default {
    name: "DeckArcs",

    props: {
        // raw mapbox-gl map instance
        map: { type: Object, required: true },

        // data
        subregions: { type: Array, required: true }, // [{id,name,lon,lat}]
        countries: { type: Array, required: true }, // [{id,name,lon,lat}]
        shipments: { type: Array, required: true }, // [{src_id,dst_id,volume}]

        // styling
        srcColor: { type: Array, default: () => [255, 0, 0, 255] },
        dstColor: { type: Array, default: () => [0, 0, 0, 255] },
    },

    data: () => ({
        _deck: null,
        _onMove: null,
        _onResize: null,
    }),

    computed: {
        _sources() {
            const m = new Map();
            for (const s of this.subregions) m.set(s["subregion_code"], s);
            return m;
        },
        _destinations() {
            const m = new Map();
            for (const c of this.countries) m.set(c["country_code"], c);
            return m;
        },
        _arcData() {
            return this.shipments
                .map((sh) => {
                    const src = this._sources.get(sh["subregion_code"]);
                    const dst = this._destinations.get(sh["country_code"]);
                    if (!src || !dst) return null;
                    return {
                        // DeckGL geometry
                        sourcePosition: [this.normalizeLon(src.lon), src.lat],
                        targetPosition: [this.normalizeLon(dst.lon), dst.lat],

                        // Existing tooltip fields
                        srcName: src.subregion_name,
                        srcCode: src.subregion_code,
                        dstName: dst.country_name,
                        dstCode: dst.country_code,

                        // Make it look like InfoPanelAir/InfoPanelSea expects
                        air_name: `${dst.country_name}`,
                        flight_type: "Country",
                        air_km: Number(sh.air_km || 0),
                        air_speed: Number(sh.air_speed || 0),
                        sea_name: `${dst.country_name}`,
                        channel_type: "Country",
                        sea_km: Number(sh.sea_km || 0),
                        sea_speed: Number(sh.sea_speed || 0),

                        link_id:
                            sh.link_id ||
                            `${src.subregion_code}-${dst.country_code}`,

                        // Same metric names as normal links
                        trailer_loads: Number(sh.trailer_loads || 0),
                        tonnes: Number(sh.tonnes || 0),
                        trip_transport_costs: Number(
                            sh.trip_transport_costs || 0,
                        ),
                        trip_freight_value: Number(sh.trip_freight_value || 0),
                        co2_tn: Number(sh.co2_tn || 0),
                    };
                })
                .filter(Boolean);
        },
    },

    watch: {
        map: {
            immediate: true,
            handler(m) {
                if (!m || typeof m.getContainer !== "function") return;
                this._mountDeck();
            },
        },
        shipments: {
            deep: true,
            handler() {
                this._updateLayers();
            },
        },
        subregions: {
            deep: true,
            handler() {
                this._updateLayers();
            },
        },
        countries: {
            deep: true,
            handler() {
                this._updateLayers();
            },
        },
    },

    beforeDestroy() {
        if (this.map && this._onMove) this.map.off("move", this._onMove);
        if (this.map && this._onResize) this.map.off("resize", this._onResize);
        this._onMove = this._onResize = null;

        if (this._deck) {
            try {
                this._deck.finalize();
            } catch {}
            // Remove the injected canvas
            const canvas = this._deck.canvas;
            if (canvas && canvas.parentNode)
                canvas.parentNode.removeChild(canvas);
            this._deck = null;
        }
    },

    methods: {
        normalizeLon(lon) {
            return ((((Number(lon) + 180) % 360) + 360) % 360) - 180;
        },

        _makeArcLayer() {
            return new ArcLayer({
                id: "international-arcs",
                data: this._arcData,
                wrapLongitude: true,
                pickable: true,
                parameters: { depthTest: false }, // draw on top
                getSourcePosition: (d) => d.sourcePosition,
                getTargetPosition: (d) => d.targetPosition,
                getSourceColor: () => this.srcColor,
                getTargetColor: () => this.dstColor,
                //getWidth: d => Math.max(0.8, Math.sqrt(d.volume) * 0.15),
                //getHeight: d => 200 + Math.sqrt(d.volume) * 500,  // curve peak in meters
                //getWidth: d => Math.max(0.4, Math.sqrt(d.volume) * 0.005),
                getWidth: 1,
                getHeight: 0.5, // almost flat; add curvature later if you like
                greatCircle: true,

                onHover: (info) => {
                    const canvas = this._deck && this._deck.canvas;
                    if (canvas) {
                        // Change cursor when hovering an arc.
                        canvas.style.cursor = info.object ? "pointer" : "";
                    }

                    // Enable click-through when not over an arc.
                    if (canvas)
                        canvas.style.pointerEvents = info.object
                            ? "auto"
                            : "none";

                    // Forward to parent if needed.
                    this.$emit("arc-hover", info);
                },

                onClick: (info) => {
                    this.$emit("arc-click", info);
                },
            });
        },

        //_syncViewState() {
        //    return {
        //        longitude: this.map.getCenter().lng,
        //        latitude: this.map.getCenter().lat,
        //        zoom: this.map.getZoom(),
        //        bearing: this.map.getBearing(),
        //        pitch: this.map.getPitch()
        //    }
        //},
        _syncViewState() {
            const center = this.map.getCenter();
            const lng =
                typeof center.wrap === "function"
                    ? center.wrap().lng
                    : center.lng;
            return {
                longitude: lng,
                latitude: center.lat,
                zoom: this.map.getZoom(),
                bearing: this.map.getBearing(),
                pitch: this.map.getPitch(),
            };
        },

        _mountDeck() {
            if (this._deck) return;
            const container = this.map.getContainer();

            this._deck = new Deck({
                parent: container,
                controller: false,
                layers: [this._makeArcLayer()],
                parameters: { depthTest: false },
                useDevicePixels: 1,
                viewState: this._syncViewState(),

                // <-- Deck's built-in tooltip
                getTooltip: ({ object }) => {
                    if (!object) return null;

                    const costs =
                        object.trip_transport_costs > 0
                            ? "$" + object.trip_transport_costs.toLocaleString()
                            : "N/A";
                    const value =
                        object.trip_freight_value > 0
                            ? "$" + object.trip_freight_value.toLocaleString()
                            : "N/A";
                    const flagCode = object.dstCode.slice(0, 2).toLowerCase(); // e.g. 'au', 'us', 'br'

                    const showLimitedMetrics =
                        costs === "N/A" && value === "N/A";

                    const limitedMetricsHtml = showLimitedMetrics
                        ? `<div style="color:red">Note: Limited metrics are available when a month filter is applied.</div>`
                        : ``;

                    return {
                        html: `
                                  <div style="
                                    position: relative;
                                    transform: translate(-50%, calc(-100% - 8px)); /* center + above cursor */
                                    background: white; color: #111;
                                    padding: 6px 8px; border-radius: 4px;
                                    box-shadow: 0 2px 8px rgba(0,0,0,0.15);
                                    font: 12px/1.3 system-ui, sans-serif;
                                    pointer-events: none;
                                  ">
                                    <div style="display:flex; align-items:center; gap:6px;">
                                      <b>${object.dstName}</b>
                                      <span class="fi fi-${flagCode}" style="width:20px;height:15px;"></span>
                                    </div>

                                    ${limitedMetricsHtml}

                                    <div>Value: ${value}</div>
                                    <div>Costs: ${costs}</div>
                                    <div style="
                                      position:absolute; left:50%; bottom:-6px; transform:translateX(-50%);
                                      width:0; height:0; border-left:6px solid transparent;
                                      border-right:6px solid transparent; border-top:6px solid white;">
                                    </div>
                                  </div>
                                `,
                        // Outer deck tooltip container: make it invisible + non-blocking
                        style: {
                            background: "transparent",
                            padding: 0,
                            border: "none",
                            boxShadow: "none",
                            width: 200,
                            height: 0,
                            overflow: "visible",
                            pointerEvents: "none",
                            zIndex: 1000,
                        },
                    };
                },
                pickingRadius: 6, // easier to hover thin arcs
            });

            // overlay styling; start with pointerEvents none (we toggle on hover)
            const canvas = this._deck.canvas;
            canvas.style.position = "absolute";
            canvas.style.top = "0";
            canvas.style.left = "0";
            canvas.style.width = "100%";
            canvas.style.height = "100%";
            canvas.style.pointerEvents = "none"; // toggled in onHover
            canvas.style.zIndex = "1";

            // keep deck camera in sync (your existing code)
            this._onMove = () => {
                if (!this._deck) return;
                this._deck.setProps({ viewState: this._syncViewState() });
                this._deck.redraw(true);
            };
            this.map.on("move", this._onMove);

            this._onResize = () => {
                if (!this._deck) return;
                this._deck.setProps({ viewState: this._syncViewState() });
                this._deck.redraw(true);
            };
            this.map.on("resize", this._onResize);
        },
        _updateLayers() {
            if (!this._deck) return;
            this._deck.setProps({ layers: [this._makeArcLayer()] });
            this._deck.redraw(true);
        },
    },
};
</script>
